<template>
  <div class="date-picker">
    <span class="icon">&#x1f4c5;</span>
    <input v-model="selectedFormatted" readonly type="text" aria-label="date" :placeholder="placeholder" @blur="isCalendar = false" @click="open" @focus="open" @keyup.down="selectMove(7)" @keyup.left="selectMove(-1)" @keyup.right="selectMove(1)" @keyup.up="selectMove(-7)" />
    <div v-if="isCalendar" class="calendar">
      <div class="navs">
        <a class="prev" :class="{ off: dateCurrentIsMin }" @mousedown.prevent="prev">&#x2190;</a>
        <span class="month">{{ monthName(dateCurrent) }}</span>
        <a class="next" :class="{ off: dateCurrentIsMax }" @mousedown.prevent="next">&#x2192;</a>
      </div>
      <div class="days">
        <div v-for="(day, index) in ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']" :key="'name' + index" class="name">
          {{ day }}
        </div>
        <div v-for="(day, index) in daysEmptyStart(dateCurrent)" :key="'emptyStart' + index" class="day empty" />
        <div v-for="(day, index) in daysInMonth(dateCurrent)" :key="index" class="day" :class="{ on: dayIsOn(day), selected: dayIsSelected(day) }" @mousedown.prevent="select(day, true)">
          {{ day.getDate() }}
        </div>
        <div v-for="(day, index) in daysEmptyEnd(dateCurrent)" :key="'emptyEnd' + index" class="day empty" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";

// Props
const props = defineProps({
  date: [Date, String],
  placeholder: String,
  min: [Date, String],
  max: [Date, String],
});

// Emit event
const emit = defineEmits(["update:date"]);

// Reactive state
const isCalendar = ref(false);
const month = ref("");
const selected = ref("");
const year = ref("");

// Computed properties
const dateCurrent = computed(() => {
  return new Date(year.value, month.value, 1, 0, 0);
});

const dateCurrentIsMax = computed(() => {
  let maxYear = dateMax.value.getFullYear();
  let maxMonth = dateMax.value.getMonth();
  return year.value == maxYear && month.value == maxMonth;
});

const dateCurrentIsMin = computed(() => {
  let minYear = dateMin.value.getFullYear();
  let minMonth = dateMin.value.getMonth();
  return year.value == minYear && month.value == minMonth;
});

const dateMax = computed(() => {
  if (props.max) return props.max;
  let today = new Date(todayDate.value);
  let year = todayDate.value.getFullYear();
  return new Date(today.setFullYear(year + 1));
});

const dateMin = computed(() => {
  return props.min instanceof Date ? props.min : todayDate.value;
});

const selectedFormatted = computed(() => {
  if (selected.value === "") return "";
  let day = new Intl.DateTimeFormat("en", { weekday: "short" }).format(selected.value);
  let date = selected.value.getDate();
  let month = new Intl.DateTimeFormat("en", { month: "short" }).format(selected.value);
  let year = new Intl.DateTimeFormat("en", { year: "numeric" }).format(selected.value);

  return `${day}, ${date} ${month} ${year}`;
});

const todayDate = computed(() => {
  let today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
});

// Watchers
watch(dateMin, () => {
  if (selected.value === "") {
    year.value = dateMin.value.getFullYear();
    month.value = dateMin.value.getMonth();
  }
});

// Lifecycle hook
onMounted(() => {
  let dt;
  if (props.date !== "") {
    dt = props.date;
    selected.value = dt;
  } else {
    dt = new Date();
  }
  year.value = dt.getFullYear();
  month.value = dt.getMonth();
});

const daysEmptyEnd = dt => {
  let year = dt.getFullYear();
  let month = dt.getMonth();
  let dayCount = new Date(year, month + 1, 0).getDate();
  let last = new Date(year, month, dayCount);
  return 6 - last.getDay();
};

const daysEmptyStart = dt => {
  let year = dt.getFullYear();
  let month = dt.getMonth();
  let first = new Date(year, month, 1);
  return first.getDay();
};

const daysInMonth = dt => {
  let days = [];
  let year = dt.getFullYear();
  let month = dt.getMonth();
  let dayCount = new Date(year, month + 1, 0).getDate();
  for (let i = 1; i <= dayCount; i++) {
    days.push(new Date(year, month, i, 0, 0, 0));
  }
  return days;
};

const dayIsOn = dt => {
  return !(dt < dateMin.value || dt > dateMax.value);
};

const dayIsSelected = dt => {
  return selected.value instanceof Date && dt.getTime() === selected.value.getTime();
};

const monthName = dt => {
  let month = new Intl.DateTimeFormat("en", { month: "long" }).format(dt);
  let year = dt.getFullYear();
  return `${month} ${year}`;
};

const next = () => {
  if (dateCurrentIsMax.value) return;
  year.value = month.value >= 12 ? year.value + 1 : year.value;
  month.value = month.value >= 12 ? 1 : month.value + 1;
};

const open = () => {
  isCalendar.value = true;
  if (selected.value === "" || selected.value < dateMin.value) {
    select(dateMin.value);
  }
};

const prev = () => {
  if (dateCurrentIsMin.value) return;
  year.value = month.value <= 0 ? year.value - 1 : year.value;
  month.value = month.value <= 0 ? 12 : month.value - 1;
};

const select = (dt, isClose) => {
  if (dt >= dateMin.value && dt <= dateMax.value) {
    selected.value = dt;
    year.value = dt.getFullYear();
    month.value = dt.getMonth();
    emit("update:date", dt);
    isCalendar.value = !isClose;
  }
};

const selectMove = days => {
  let dt = new Date(selected.value);
  dt.setDate(dt.getDate() + days);
  select(dt, false);
};
</script>
